import { SettingsFactory } from '../utils/SettingsFactory'
import exceptionMixin from '@/common/mixins/exception-mixin'
import { mapActions, mapState } from 'vuex'
import { wasSkipped, wasCancelledByUser, skipWarning } from '@/common/utils/save-result'

export const settingsFactoryMixin = {
  mixins: [exceptionMixin],
  computed: {
    ...mapState('login', ['activeLocation']),
    isOverride () {
      return SettingsFactory.instance.isOverride
    },
    configLevel () {
      return !this.activeLocation
        ? 'company'
        : this.activeLocation.type === 'GROUP'
          ? 'group'
          : 'location'
    },
    configLevelClass () {
      return this.configLevel + '-mode'
    }

  },
  methods: {
    ...mapActions('menuPublish', { retrieveSyncData: 'retrieveData' }),
    retrieveSettings () {
      try {
        return this.getSettingsFactory().retrieveSettings()
      } catch (error) {
        if (this.isUserError(error) && error[0].code === 'NO_MENU_SET') {
          this.alertError('The Location does not have a Menu Assigned.')
        } else {
          this.handleError(error, 'Unable to load Menu, please try again later.')
        }
        this.$router.push({ name: 'home' })
        return Promise.reject(error)
      }
    },
    retrieveEmployment () {
      try {
        return this.getSettingsFactory().retrieveEmployment()
      } catch (error) {
        this.handleError(error, 'Unable to load employment, please try again later.')
        this.$router.push({ name: 'home' })
        return Promise.reject(error)
      }
    },
    getSettingsFactory () {
      return SettingsFactory.instance
    },
    getMenuBlob () {
      return SettingsFactory.instance.menuBlob
    },
    getMenuBlobBase () {
      return SettingsFactory.instance.menuBlobBase
    },
    getSettingsBlob () {
      return SettingsFactory.instance.settingsBlob
    },
    getSettingsBlobBase () {
      return SettingsFactory.instance.settingsBlobBase
    },
    getEmploymentBlob () {
      return SettingsFactory.instance.employmentBlob
    },
    getEmploymentBlobBase () {
      return SettingsFactory.instance.employmentBlobBase
    },
    async saveMenu ({ title = 'Menu', plural = false, showMessages = true } = {}) {
      if (showMessages) {
        this.showLoading(`Please wait while the ${title} ${plural ? 'are' : 'is'} being saved.`)
      }

      try {
        await this.getSettingsFactory().saveMenu()
        await this.retrieveSyncData('menu')
        if (showMessages) this.alertSuccess(`The ${title} ${plural ? 'have' : 'has'} been saved correctly.`)
      } catch (error) {
        this.handleError(error, `Unable to save the ${title}. Please try again later.`)
        return Promise.reject(error)
      } finally {
        if (showMessages) this.hideLoading()
      }
    },
    async saveLocationSettings ({ title = 'Location Settings', plural = false, showMessages = true } = {}) {
      if (showMessages) {
        this.showLoading(`Please wait while the ${title} ${plural ? 'are' : 'is'} being saved.`)
      }

      try {
        const result = await this.getSettingsFactory().saveLocationSettings()
        // TPOS-10162: saveLocationSettings() resolves with { skipped: true } (rather than
        // saving) when the location changed out from under this save — don't tell the user
        // it saved, don't refresh sync data for a save that didn't happen, and hand the result
        // back so callers that reset their own local dirty-state baseline (e.g. Stations.vue's
        // updateOriginalStations()) can skip that too.
        // TPOS-10162 PR review (3rd round): a skip used to show nothing at all (when
        // showMessages is true) — the user got no indication their changes were discarded.
        // TPOS-10162 PR review (4th round): and then it claimed a location change for every skip,
        // including the user declining the ">6 changes" confirm. Branch on the reason instead.
        if (wasSkipped(result)) {
          if (showMessages && !wasCancelledByUser(result)) {
            this.alertWarning(skipWarning(result, title))
          }
        } else {
          await this.retrieveSyncData('menu')
          if (showMessages) this.alertSuccess(`The ${title} ${plural ? 'have' : 'has'} been saved correctly.`)
        }
        return result
      } catch (error) {
        this.handleError(error, `Unable to save the ${title}. Please try again later.`)
      } finally {
        if (showMessages) this.hideLoading()
      }
    },
    async saveEmployment ({ title = 'Employment', plural = false, showMessages = true } = {}) {
      if (showMessages) {
        this.showLoading(`Please wait while the ${title} ${plural ? 'are' : 'is'} being saved.`)
      }

      try {
        // TPOS-10162 PR review (3rd round): this used to skip straight to "saved correctly"
        // regardless of what saveEmployment() resolved with — the only one of this mixin's
        // three save methods that never checked for { skipped: true } at all.
        const result = await this.getSettingsFactory().saveEmployment()
        if (wasSkipped(result)) {
          if (showMessages && !wasCancelledByUser(result)) {
            this.alertWarning(skipWarning(result, title))
          }
        } else {
          await this.retrieveSyncData('employment')
          if (showMessages) this.alertSuccess(`The ${title} ${plural ? 'have' : 'has'} been saved correctly.`)
        }
        return result
      } catch (error) {
        this.handleError(error, `Unable to save the ${title}. Please try again later.`)
      } finally {
        if (showMessages) this.hideLoading()
      }
    },
    setActiveMenu (menuId) {
      this.getSettingsFactory().menuId = menuId
    }
  }
}
